// src/routes/AppRoutes.jsx
import { useContext } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Login from "../pages/auth/Login";
import SignUp from "../pages/auth/SignUp";
import ResetPassword from "../pages/auth/ResetPassword";
import DashboardRedirect from "../pages/common/DashboardRedirect";
import DocumentsLibrary from "../pages/common/DocumentsLibrary";
import AdminCalendar from "../pages/admin/AdminCalendar";
import AdminLiveMap from "../pages/admin/AdminLiveMap";
import AssignJobsTab from "../pages/admin/AssignJobsTab";
import AdminDataHub from "../pages/admin/AdminDataHub";
import AdminDriverView from "../pages/admin/AdminDriverView";
import AdminManagerAssignments from "../pages/admin/AdminManagerAssignments";
import AdminTimesheetApprovals from "../pages/admin/AdminTimesheetApprovals";
import NotAuthorized from "../pages/NotAuthorized";
import { UserContext } from "../contexts/UserContext";
import { hasPrivilegedAccess } from "../utils/privilegedAccess";

// Driver in-app router (existing)
import DriverJobRouter from "../pages/driver/DriverJobRouter";
import DriverCalendar from "../pages/driver/DriverCalendar";
import DriverTimesheet from "../pages/driver/DriverTimesheet";
import DriverIncidentList from "../pages/incidents/DriverIncidentList";
import DriverIncidentEditor from "../pages/incidents/DriverIncidentEditor";
import AdminIncidentQueue from "../pages/incidents/AdminIncidentQueue";
import AdminIncidentReview from "../pages/incidents/AdminIncidentReview";

// Admin job detail page
import JobDetailPage from "../pages/admin/JobDetailPage";

/* ---------- Guards ---------- */
function RequireAdmin({ children }) {
  const { userInfo, isLoading } = useContext(UserContext) || {};
  const location = useLocation();

  if (isLoading) return null;

  if (!userInfo) return <Navigate to="/login" replace state={{ from: location }} />;
  const role = String(userInfo.role || "").toLowerCase();
  if (role !== "admin") return <Navigate to="/dashboard" replace />;

  return children;
}

function RequireAuth({ children }) {
  const { userInfo, isLoading } = useContext(UserContext) || {};
  const location = useLocation();

  if (isLoading) return null;

  if (!userInfo) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function RequirePrivilegedAccess({ children }) {
  const { userInfo, isLoading } = useContext(UserContext) || {};
  const location = useLocation();

  if (isLoading) return null;

  if (!userInfo) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!hasPrivilegedAccess(userInfo)) return <Navigate to="/not-authorized" replace />;

  return children;
}

/* ---------- Role-aware Job route ---------- */
/** If user is admin -> Admin JobDetailPage, else -> DriverJobRouter */
function JobRouteSwitcher() {
  const { userInfo } = useContext(UserContext) || {};
  const role = String(userInfo?.role || "").toLowerCase();
  if (role === "admin") {
    return <JobDetailPage />;
  }
  return <DriverJobRouter />;
}

export default function AppRoutes({ scriptLoaded }) {
  return (
    <>
      <Navbar />
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<Login />} />
        <Route
          path="/signup"
          element={
            <RequirePrivilegedAccess>
              <SignUp />
            </RequirePrivilegedAccess>
          }
        />
        <Route
          path="/admin/reset-password"
          element={
            <RequirePrivilegedAccess>
              <ResetPassword />
            </RequirePrivilegedAccess>
          }
        />
        <Route path="/not-authorized" element={<NotAuthorized />} />

        {/* Common/Admin */}
        <Route path="/dashboard" element={<DashboardRedirect scriptLoaded={scriptLoaded} />} />
        <Route
          path="/documents"
          element={
            <RequireAuth>
              <DocumentsLibrary />
            </RequireAuth>
          }
        />
        <Route path="/calendar" element={<AdminCalendar />} />
        <Route path="/admin/live-map" element={<AdminLiveMap />} />
        <Route path="/assign" element={<AssignJobsTab scriptLoaded={scriptLoaded} />} />

        {/* Admin-only pages */}
        <Route
          path="/admin/driver-view"
          element={
            <RequireAdmin>
              <AdminDriverView />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/data"
          element={
            <RequireAdmin>
              <AdminDataHub />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/managers"
          element={
            <RequireAdmin>
              <AdminManagerAssignments />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/timesheets"
          element={
            <RequireAdmin>
              <AdminTimesheetApprovals />
            </RequireAdmin>
          }
        />


        {/* Admin Job details (explicit admin alias kept for robustness) */}
        <Route
          path="/admin/job/:jobId"
          element={
            <RequireAdmin>
              <JobDetailPage />
            </RequireAdmin>
          }
        />

        <Route
          path="/driver/calendar"
          element={
            <RequireAuth>
              <DriverCalendar />
            </RequireAuth>
          }
        />
        <Route
          path="/driver/timesheet"
          element={
            <RequireAuth>
              <DriverTimesheet />
            </RequireAuth>
          }
        />
        <Route
          path="/driver/incidents"
          element={
            <RequireAuth>
              <DriverIncidentList />
            </RequireAuth>
          }
        />
        <Route
          path="/driver/incidents/new"
          element={
            <RequireAuth>
              <DriverIncidentEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/driver/incidents/:incidentId"
          element={
            <RequireAuth>
              <DriverIncidentEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/incidents"
          element={
            <RequireAdmin>
              <AdminIncidentQueue />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/incidents/:incidentId"
          element={
            <RequireAdmin>
              <AdminIncidentReview />
            </RequireAdmin>
          }
        />


        {/* Unified Job route:
            - Auth required for everyone
            - Admins see admin JobDetailPage
            - Drivers see driver job UI
        */}
        <Route
          path="/job/:jobId"
          element={
            <RequireAuth>
              <JobRouteSwitcher />
            </RequireAuth>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}
